import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/api';
import { confirmAsync } from '../utils/notify';
import './FacebookConnection.css';

/**
 * Facebook Page Connection component.
 *
 * Uses Meta OAuth 2.0 (facebook.com/v24.0/dialog/oauth) in a popup window.
 * Mirrors the TikTokConnection component pattern.
 *
 * Flow:
 * 1. Frontend opens Facebook OAuth URL in popup
 * 2. User authorizes and grants Page permissions
 * 3. Facebook redirects to backend callback with authorization code
 * 4. Backend exchanges code → long-lived token → discovers Pages
 * 5. If 1 Page: auto-select. If multiple: Page selector UI in popup
 * 6. Backend saves FacebookPage, subscribes Messenger webhook
 * 7. Popup sends postMessage (FACEBOOK_OAUTH_CALLBACK) to parent
 * 8. Frontend receives message and refreshes account status
 */
function FacebookConnection() {
  const { t } = useTranslation();
  const popupRef = useRef(null);
  const popupCheckIntervalRef = useRef(null);

  const [connectedAccount, setConnectedAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [togglingMessenger, setTogglingMessenger] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Fetch connected account status
  const fetchAccountStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get('/api/v1/social/facebook/account');
      setConnectedAccount(response.data);
    } catch (err) {
      if (err.response?.status !== 401 && err.response?.status !== 404) {
        setError(t('facebook.errorFetching'));
      }
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchAccountStatus();
  }, [fetchAccountStatus]);

  // Listen for OAuth callback message from popup
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;

      const data = event.data;
      if (data?.type === 'FACEBOOK_OAUTH_CALLBACK') {
        if (popupCheckIntervalRef.current) {
          clearInterval(popupCheckIntervalRef.current);
          popupCheckIntervalRef.current = null;
        }

        if (data.success) {
          setSuccess(t('facebook.connectSuccess', { name: data.page_name || 'Facebook Page' }));
          fetchAccountStatus();
        } else if (data.error) {
          setError(data.error);
        }
        setConnecting(false);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [t, fetchAccountStatus]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (popupCheckIntervalRef.current) {
        clearInterval(popupCheckIntervalRef.current);
      }
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }
    };
  }, []);

  // Start Facebook OAuth in popup
  const handleConnect = async () => {
    setConnecting(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await apiClient.get('/api/v1/social/facebook/oauth/start');
      const { oauth_url } = response.data;

      const width = 600;
      const height = 750;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }

      popupRef.current = window.open(
        oauth_url,
        'facebook_oauth',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
      );

      if (!popupRef.current) {
        setError(t('facebook.errors.popupBlocked'));
        setConnecting(false);
        return;
      }

      if (popupCheckIntervalRef.current) {
        clearInterval(popupCheckIntervalRef.current);
      }

      popupCheckIntervalRef.current = setInterval(() => {
        if (popupRef.current && popupRef.current.closed) {
          clearInterval(popupCheckIntervalRef.current);
          popupCheckIntervalRef.current = null;
          setConnecting((current) => {
            if (current) {
              setError(t('facebook.errors.loginCancelled'));
              return false;
            }
            return current;
          });
        }
      }, 500);

    } catch (err) {
      console.error('Failed to start Facebook OAuth:', err);
      setError(t('facebook.errors.connectionFailed'));
      setConnecting(false);
    }
  };

  // Disconnect account
  const handleDisconnect = async () => {
    if (!(await confirmAsync({ message: t('facebook.confirmDisconnect'), danger: true }))) return;

    setDisconnecting(true);
    setError(null);
    try {
      await apiClient.delete('/api/v1/social/facebook/disconnect');
      setConnectedAccount(null);
      setSuccess(t('facebook.disconnectSuccess'));
    } catch (err) {
      const message = err.response?.data?.detail || t('facebook.errorDisconnecting');
      setError(message);
    } finally {
      setDisconnecting(false);
    }
  };

  // Refresh page info
  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const response = await apiClient.post('/api/v1/social/facebook/refresh');
      if (response.data.account) {
        setConnectedAccount((prev) => ({
          ...prev,
          ...response.data.account,
        }));
      }
      setSuccess(t('facebook.refreshSuccess'));
    } catch (err) {
      const message = err.response?.data?.detail || t('facebook.errorRefreshing');
      setError(message);
    } finally {
      setRefreshing(false);
    }
  };

  // Toggle Messenger
  const handleToggleMessenger = async () => {
    setTogglingMessenger(true);
    setError(null);
    try {
      const response = await apiClient.post('/api/v1/social/facebook/messenger/toggle');
      setConnectedAccount((prev) => ({
        ...prev,
        messenger_enabled: response.data.messenger_enabled,
      }));
      setSuccess(
        response.data.messenger_enabled
          ? t('facebook.messengerEnabled')
          : t('facebook.messengerDisabled')
      );
    } catch (err) {
      const message = err.response?.data?.detail || t('facebook.errorToggleMessenger');
      setError(message);
    } finally {
      setTogglingMessenger(false);
    }
  };

  // Clear messages after timeout
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 10000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  if (loading) {
    return (
      <div className="facebook-connection loading">
        <div className="spinner" />
        <span>{t('common.loading')}</span>
      </div>
    );
  }

  return (
    <div className="facebook-connection">
      <div className="section-header">
        <div className="header-icon">📘</div>
        <div className="header-text">
          <h2>{t('facebook.connectionTitle')}</h2>
          <p className="description">{t('facebook.connectionDescription')}</p>
        </div>
      </div>

      {/* Success Message */}
      {success && (
        <div className="alert alert-success">
          <span className="icon">✓</span>
          <span>{success}</span>
          <button onClick={() => setSuccess(null)} className="dismiss">×</button>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="alert alert-error">
          <span className="icon">⚠️</span>
          <span>{error}</span>
          <button onClick={() => setError(null)} className="dismiss">×</button>
        </div>
      )}

      {connectedAccount ? (
        /* Connected Account View */
        <div className="connected-account">
          <div className="account-card">
            <div className="account-info">
              {connectedAccount.page_picture_url ? (
                <img
                  src={connectedAccount.page_picture_url}
                  alt={connectedAccount.page_name}
                  className="profile-picture"
                />
              ) : (
                <div className="profile-picture placeholder">
                  <span>📘</span>
                </div>
              )}
              <div className="account-details">
                <h3>{connectedAccount.page_name || 'Facebook Page'}</h3>
                {connectedAccount.page_category && (
                  <span className="page-category">{connectedAccount.page_category}</span>
                )}
                <div className="stats">
                  {connectedAccount.followers_count != null && (
                    <span className="stat">
                      <strong>{connectedAccount.followers_count.toLocaleString()}</strong> {t('facebook.followers')}
                    </span>
                  )}
                  {connectedAccount.fan_count != null && (
                    <span className="stat">
                      <strong>{connectedAccount.fan_count.toLocaleString()}</strong> {t('facebook.fans')}
                    </span>
                  )}
                </div>
                <p className="connected-date">
                  {t('facebook.connectedOn', {
                    date: new Date(connectedAccount.connected_at).toLocaleDateString(),
                  })}
                </p>
                {connectedAccount.last_post_at && (
                  <p className="last-post">
                    {t('facebook.lastPost', {
                      date: new Date(connectedAccount.last_post_at).toLocaleDateString(),
                    })}
                  </p>
                )}
              </div>
              <div className={`status-badge ${connectedAccount.is_active && connectedAccount.token_valid ? 'active' : 'inactive'}`}>
                {connectedAccount.is_active && connectedAccount.token_valid
                  ? t('facebook.active')
                  : t('facebook.inactive')}
              </div>
            </div>

            {/* Messenger Toggle */}
            <div className="messenger-toggle">
              <div className="messenger-info">
                <span className="messenger-icon">💬</span>
                <div>
                  <strong>{t('facebook.messengerTitle')}</strong>
                  <p className="messenger-description">{t('facebook.messengerDescription')}</p>
                </div>
              </div>
              <button
                className={`toggle-btn ${connectedAccount.messenger_enabled ? 'enabled' : ''}`}
                onClick={handleToggleMessenger}
                disabled={togglingMessenger}
              >
                <span className="toggle-track">
                  <span className="toggle-thumb" />
                </span>
                <span className="toggle-label">
                  {connectedAccount.messenger_enabled ? t('facebook.enabled') : t('facebook.disabled')}
                </span>
              </button>
            </div>

            {!connectedAccount.token_valid && (
              <div className="token-warning">
                <span className="icon">⚠️</span>
                <span>{t('facebook.tokenInvalid')}</span>
              </div>
            )}

            <div className="account-actions">
              <button
                className="btn btn-secondary"
                onClick={handleRefresh}
                disabled={refreshing}
                title={t('facebook.refreshInfo')}
              >
                {refreshing ? '...' : '🔄'} {t('facebook.refresh')}
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDisconnect}
                disabled={disconnecting}
              >
                {disconnecting ? t('common.processing') : t('facebook.disconnect')}
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* No Account Connected */
        <div className="no-account">
          <div className="connect-prompt">
            <div className="icon">📄</div>
            <h3>{t('facebook.noAccountConnected')}</h3>
            <p>{t('facebook.connectPrompt')}</p>
            <ul className="requirements">
              <li>{t('facebook.requirement1')}</li>
              <li>{t('facebook.requirement2')}</li>
              <li>{t('facebook.requirement3')}</li>
            </ul>
          </div>

          <button
            className="btn btn-primary btn-large facebook-connect-btn"
            onClick={handleConnect}
            disabled={connecting}
          >
            {connecting ? (
              <>
                <span className="spinner-small" />
                {t('facebook.connecting')}
              </>
            ) : (
              <>
                📘 {t('facebook.connectButton')}
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

export default FacebookConnection;
